/**
 * CommandPalette — global Cmd+K / Ctrl+K search (GET /v1/search).
 *
 * Mounted once in Shell. Opens via keyboard shortcut or the top-bar chip
 * (which dispatches the `open-command-palette` window event); closes on
 * Escape, backdrop click, or route change. Input is debounced ~280ms;
 * ↑/↓ move through a flat index across all groups, Enter opens.
 */

import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import type { SearchGroup, SearchGroupType } from '@1financial/shared';
import { clsx } from 'clsx';
import { getAccessToken, authenticatedFetch as fetch } from '../lib/auth-context.js';
import { httpError } from '../lib/http-error.js';
import { useEscape } from '../hooks/useEscape.js';

const GROUP_ICONS: Record<SearchGroupType, string> = {
  clients: '◇',
  engagements: '⬡',
  complianceClients: '⬢',
  complianceItems: '▣',
  proposals: '✉',
  invoices: '◈',
  users: '◉',
  goals: '◎',
  tags: '#',
};

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Cmd+K / Ctrl+K toggles; the TopBar chip dispatches the custom event.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('open-command-palette', onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('open-command-palette', onOpen);
    };
  }, []);

  // Close on route change.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Fresh state + focus each time it opens.
  useEffect(() => {
    if (open) {
      setQuery('');
      setDebounced('');
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // Debounce the search term.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 280);
    return () => clearTimeout(t);
  }, [query]);

  useEscape(() => setOpen(false), open);

  const search = useQuery({
    queryKey: ['global-search', debounced],
    enabled: open && debounced.length >= 2,
    queryFn: async () => {
      const res = await fetch(`/v1/search?q=${encodeURIComponent(debounced)}`, {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { groups: SearchGroup[] } };
      return body.data.groups;
    },
  });

  const groups = search.data ?? [];
  const flat = groups.flatMap((g) => g.items.map((item) => ({ ...item, groupType: g.type })));

  // Reset + clamp the active row as results change.
  useEffect(() => {
    setActive(0);
  }, [debounced]);
  useEffect(() => {
    if (active > flat.length - 1) setActive(Math.max(flat.length - 1, 0));
  }, [flat.length, active]);
  useEffect(() => {
    document.getElementById(`cp-item-${active}`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  function openItem(index: number) {
    const item = flat[index];
    if (!item) return;
    setOpen(false);
    navigate(item.href);
  }

  function onInputKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (flat.length > 0) setActive((a) => Math.min(a + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (flat.length > 0) setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      openItem(active);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 p-4 pt-[15vh]" onClick={() => setOpen(false)}>
      <div
        className="bg-bg-panel border border-border rounded-xl w-full max-w-xl shadow-2xl animate-pop overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 border-b border-border">
          <span className="text-muted">⌕</span>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder="Search clients, engagements, items, proposals…"
            className="flex-1 bg-transparent py-3 text-sm text-white placeholder-muted focus:outline-none"
          />
          <kbd className="text-[10px] text-muted border border-border rounded px-1.5 py-0.5">esc</kbd>
        </div>

        <div className="max-h-[50vh] overflow-y-auto">
          {debounced.length < 2 && (
            <p className="px-4 py-8 text-center text-sm text-muted">Type at least 2 characters to search…</p>
          )}
          {debounced.length >= 2 && search.isLoading && (
            <div className="p-4 space-y-2">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-9 rounded-lg bg-bg-hover/60 animate-pulse" />
              ))}
            </div>
          )}
          {debounced.length >= 2 && !search.isLoading && search.error && (
            <p className="px-4 py-8 text-center text-sm text-rose-400">{(search.error as Error).message}</p>
          )}
          {debounced.length >= 2 && !search.isLoading && !search.error && flat.length === 0 && (
            <p className="px-4 py-8 text-center text-sm text-muted">No results for “{debounced}”.</p>
          )}

          {(() => {
            let idx = -1;
            return groups.map((g) => (
              <div key={g.type}>
                <div className="px-4 pt-3 pb-1 flex items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-muted">
                  <span>{g.label}</span>
                  {g.total > g.items.length && <span className="normal-case font-normal">{g.total} total</span>}
                </div>
                {g.items.map((item) => {
                  idx += 1;
                  const i = idx;
                  return (
                    <button
                      key={item.id}
                      id={`cp-item-${i}`}
                      onMouseEnter={() => setActive(i)}
                      onClick={() => openItem(i)}
                      className={clsx(
                        'w-full flex items-center gap-3 px-4 py-2 text-left',
                        i === active ? 'bg-bg-hover' : '',
                      )}
                    >
                      <span className="w-5 shrink-0 text-center text-accent text-xs">{GROUP_ICONS[g.type]}</span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm text-white truncate">{item.title}</span>
                        {item.subtitle && <span className="block text-xs text-muted truncate">{item.subtitle}</span>}
                      </span>
                    </button>
                  );
                })}
              </div>
            ));
          })()}
        </div>

        <div className="flex items-center gap-4 px-4 py-2 border-t border-border text-[11px] text-muted">
          <span>↑↓ navigate</span>
          <span>↵ open</span>
          <span>esc close</span>
        </div>
      </div>
    </div>
  );
}
